import { useState, useEffect, useCallback } from 'react';
import { getUserMap, saveUserItem, deleteUserItem } from '../data/userStore';
import { RESERVED_PURCHASE_KEYS } from '../data/entitlements';
import { isInternalBuild } from '../config/distribution';

// Message rendu a l'UI quand l'achat d'un pack n'est pas disponible.
export const PACK_PURCHASE_UNAVAILABLE =
  'This pack is not available for purchase yet.';

// Le scope 'purchase' ne contient pas que des ids de packs : `full_app` (achat
// one-time) et `patreon` (acces membre, ecrit par le serveur) y vivent aussi.
// Les prendre pour des packs deverrouillerait un pack fantome et, pire, un
// clearPurchases effacerait la row Patreon.
const packIdsFrom = (map) => Object.keys(map).filter((k) => !RESERVED_PURCHASE_KEYS.includes(k));

/**
 * Hook to manage backing pack purchases (mock implementation)
 *
 * Purchases are keyed by PACK id (the pack carries the price, not the artist).
 * In production, this would integrate with:
 * - RevenueCat or StoreKit for iOS
 * - Google Play Billing for Android
 * - Receipt validation on backend
 */
const usePackPurchases = () => {
  // Initialise from the user mirror: keys = purchased pack ids
  const [purchasedPacks, setPurchasedPacks] = useState(() => new Set(packIdsFrom(getUserMap('purchase'))));
  const [isLoading, setIsLoading] = useState(true);

  // Mirror is hydrated at boot; pick up its state on mount
  useEffect(() => {
    setPurchasedPacks(new Set(packIdsFrom(getUserMap('purchase'))));
    setIsLoading(false);
  }, []);

  /**
   * Check if a pack has been purchased
   */
  const isPurchased = useCallback((packId) => {
    return purchasedPacks.has(packId);
  }, [purchasedPacks]);

  /**
   * Achat d'un pack.
   *
   * Aucun produit RevenueCat n'existe encore par pack : il n'y a donc pas de
   * flux d'achat reel sur ce chemin, seulement le mock de maquette. Un build
   * public refuse l'achat au lieu de poser l'entitlement, sinon le bouton
   * donne le pack gratuitement.
   *
   * Quand les packs seront reellement vendus : produit par pack dans
   * RevenueCat, puis passer par lib/purchases comme full_app.
   */
  const purchasePack = useCallback(async (packId, price) => {
    if (!isInternalBuild()) {
      return { success: false, packId, error: PACK_PURCHASE_UNAVAILABLE };
    }
    // Mock : simule le delai du flux d'achat natif avant de poser l'entitlement.
    return new Promise((resolve) => {
      setTimeout(() => {
        saveUserItem('purchase', packId, { purchased: true, source: 'mock' });
        setPurchasedPacks(prev => new Set([...prev, packId]));
        resolve({ success: true, packId });
      }, 500);
    });
  }, []);

  /**
   * Restore purchases (for "Restore Purchases" button)
   */
  const restorePurchases = useCallback(async () => {
    // In production: would query app store for previous purchases
    // For mockup: just return current state
    return { restored: [...purchasedPacks] };
  }, [purchasedPacks]);

  /**
   * Clear all purchases (for testing)
   */
  const clearPurchases = useCallback(() => {
    setPurchasedPacks(prev => {
      prev.forEach(id => deleteUserItem('purchase', id));
      return new Set();
    });
  }, []);

  return {
    purchasedPacks,
    isPurchased,
    purchasePack,
    restorePurchases,
    clearPurchases,
    isLoading
  };
};

export default usePackPurchases;
